import React, { useContext, useRef, useEffect, useCallback, useState } from "react";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { useGlobalState } from "hooks/useGlobalState";
import Notifications from "partials/notifications";
import UserInfo from "partials/user-info";
import ErrorNotifications from "partials/error-notifications";
import SessionContext from "SessionContext";
import { useHistory } from "react-router-dom";
import AudienceUnsavedModal from "partials/audienceUnsavedModal";

export function CenteredTemplate(props) {
  return (
    <div className="app centered-template">
      <main>{props.children}</main>
    </div>
  );
}

const keycode = { esc: 27, enter: 13 };

export function DefaultTemplate(props) {
  const { appState, crossfilterStore, chartsStore } = useContext(SessionContext);
  const { companyHasExternalDestinations } = useGlobalState();
  const access = appState.userCan;
  const navDrawer = useRef(null);
  const history = useHistory();

  const company_is_reseller = appState.activeCompany.allow_sub_companies || false;
  const show_settings_link = access('settings') && (company_is_reseller || companyHasExternalDestinations);
  const fixtureModeEnabled = localStorage.getItem('useFixtures') === 'true';
  const [showExplorerSubmenu, setShowExplorerSubmenu] = useState(false);
  const [showUnsavedExplorerModal, setShowUnsavedExplorerModal] = useState(false);
  const [explorerPath, setExplorerPath] = useState();

  const handleNavKeyUp = useCallback((ev) => {
    if (ev.which === keycode.esc || ev.which === keycode.enter) {
      ev.target.blur();
    }
  }, []);

  const handleNavClick = useCallback((ev) => {
    setShowExplorerSubmenu(false);
    ev.currentTarget.blur();
    if (navDrawer.current) navDrawer.current.style.maxWidth = "100%";
  }, []);

  const handleExplorerClick = (path) => {
    let order_unsaved = localStorage.getItem("am:order:has_unsaved_changes") === "true";
    let audience_saved = localStorage.getItem("am:audience:is_saved") !== "false";

    if(crossfilterStore.explorerModule === path && (window.location.pathname === '/explorer')) { 
      return;
    } 

    if(order_unsaved && (window.location.pathname === '/order')) {
      setExplorerPath(path);
      handleContinue(path, true);
      return; 
    } 
    
    if(!audience_saved && (window.location.pathname === '/explorer')) {
      setExplorerPath(path);
      setShowUnsavedExplorerModal(true);
      return;
    }

    handleContinue(path, false);
  };

  const handleContinue = (path=null, start_new_audience=true) => {
    const resolvedPath = path ? path : explorerPath;
    const moduleChanged = crossfilterStore.explorerModule !== resolvedPath;
    // Clear stale audience state/localStorage even when the population is unchanged,
    // since setExplorerModule is a no-op (and won't trigger its own cleanup effect) in that case.
    start_new_audience && crossfilterStore.restoreFromAudience("clear_all");
    crossfilterStore.setExplorerModule(resolvedPath);
    chartsStore.loadChartsData();
    if( start_new_audience && (window.location.pathname !== '/explorer') && moduleChanged ) {
      localStorage.setItem("am:new_explorer", "yes");
    } else {
      localStorage.setItem("am:new_explorer", "no");
    }
    localStorage.setItem('am:audience:is_saved', 'true');
    setShowUnsavedExplorerModal(false);
    setShowExplorerSubmenu(false);
    setExplorerPath("");
    if (navDrawer.current) navDrawer.current.style.maxWidth = "100%";
    history.push('/explorer');
  }

  const handleNavMouseLeave = useCallback(() => {
    setTimeout(() => {
      if (navDrawer.current) navDrawer.current.style.maxWidth = null;
    }, 100);
  }, []);

  useEffect(() => {
    const dev = 'app.dev.audiencemaker.com';
    const qa = 'app.qa.audiencemaker.com';
    const local = 'localhost';
    const url_hostname = window.location.hostname;

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    let message = document.createElement("h2");
    message.id = "nav-hostname-message";
    message.style.color = "white";
    message.style.marginTop = "15em";
    message.style.fontSize = "1em";
    message.style.textAlign = "center";
    message.innerText = "";

    if (navDrawer.current && !navDrawer.current.querySelector("#nav-hostname-message")) {
      navDrawer.current.appendChild(message);
    }

    switch (url_hostname) {
      case qa:
        navDrawer.current.style.background = window.qa_color;
        message.innerText = "QA";
        link.href = "/am/favicon-qa.ico";
        break;
      case dev:
        navDrawer.current.style.background = window.dev_color;
        message.innerText = "Dev";
        link.href = "/am/favicon-dev.ico";
        break;
      case local:
        message.innerText = "Local";
        message.style.fontSize = "0.75em";
        link.href = "/am/favicon-localhost.ico";
        break;
      default:
        link.href = "/am/favicon.ico";
        break;
    }
  }, []);

  return (
    <div className="app default-template">
      <ErrorNotifications />
      {((appState.activeCompany && !appState.activeCompany.disable_banner) || access('administration')) && (
        <header>
          <Link to="/">
            <img
              src={window.skin.header_logo}
              alt={window.skin.site_name}
              className="logo"
            />
          </Link>
          {fixtureModeEnabled && (
            <>
              <i className="spacer" />
              <span className="fixture-mode">Using Demo Data</span>
            </>
          )}
          <i className="spacer" />
          <Notifications />
          <div className="support-icon">
            <a color="link" href={`${window.support_url}`} target="_blank" rel="noreferrer">
              <i className="bi bi-question-circle"></i> <span className="tutorials-link">Knowledge Base</span>
            </a>
          </div>
          <UserInfo keepOpen={false} />
        </header>
      )}
      <main>
        <nav className="main-nav" onMouseLeave={handleNavMouseLeave}>
          <ul ref={navDrawer}>
            {access('has_explorer_view') && (
              <li className="has-submenu"
                onMouseEnter={() => setShowExplorerSubmenu(true)}
                onMouseLeave={() => setShowExplorerSubmenu(false)}
              >
                <Link to="#" 
                  className={classNames('nav-no-link', { 'is-active': appState.site_section === 'explorer' })}
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="fa fa-map" />Explorer
                </Link>
                
                {showExplorerSubmenu && (
                  <ul className="submenu">
                    {access('explorer_consumers_view') && (
                      <li>
                        <Link to="" 
                          onClick={(e) => {
                              e.preventDefault(); 
                              handleExplorerClick('consumers');
                          }}
                          className={classNames({ 'is-active': crossfilterStore.explorerModule === 'consumers' })}
                        >
                          <i className="fa-solid fa-globe" /> CONSUMER
                        </Link>
                      </li>
                    )}
                    {access('explorer_movers_view') && (
                      <li>
                        <Link to="" 
                          onClick={(e) => {
                              e.preventDefault();
                              handleExplorerClick('movers');
                          }}
                          className={classNames({ 'is-active': crossfilterStore.explorerModule === 'movers' })}
                        >
                          <i className="fa-solid fa-truck" /> MOVERS
                        </Link>
                      </li>
                    )}
                    {access('explorer_lifeevents_view') && (
                      <li>
                        <Link to="" 
                          onClick={(e) => {
                              e.preventDefault();
                              handleExplorerClick('life_events');
                          }}
                          className={classNames({ 'is-active': crossfilterStore.explorerModule === 'life_events' })}
                        >
                          <i className="fa-regular fa-calendar-days" /> LIFE EVENTS
                        </Link>
                      </li>
                    )}
                  </ul>
                )}
              </li>
            )}
            {access('audience_view') && (
              <li>
                <Link
                  to="/audiences"
                  className={classNames({ 'is-active': appState.site_section === 'audiences' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-users" />
                  My Audiences
                </Link>
              </li>
            )}
            {access('location_view') && (
              <li>
                <Link
                  to="/locations"
                  className={classNames({ 'is-active': appState.site_section === 'locations' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-location-dot" />
                  Locations
                </Link>
              </li>
            )}
            {access('upload_view') && (
              <li>
                <Link
                  to="/uploads"
                  className={classNames({ 'is-active': appState.site_section === 'uploads' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-cloud-arrow-up" />
                  Uploads
                </Link>
              </li>
            )}
            {access('model_view') && (
              <li>
                <Link
                  to="/models"
                  className={classNames({ 'is-active': appState.site_section === 'models' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-users-viewfinder" />
                  Models
                </Link>
              </li>
            )}
            {access('order_view') && (
              <li>
                <Link
                  to="/orders"
                  className={classNames({ 'is-active': appState.site_section === 'orders' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-box-archive" />
                  Orders
                </Link>
              </li>
            )}
            {access('audience_view') && (
              <li>
                <Link
                  to="/compare-list"
                  className={classNames({ 'is-active': appState.site_section === 'compare-list' })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-chart-line" />
                  Compare
                </Link>
              </li>
            )}
            {show_settings_link && (
              <li>
                <Link
                  to="/company-settings"
                  className={classNames({ "is-active": appState.site_section === "company-settings" })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-sliders" />
                  Settings
                </Link>
              </li>
            )}
            {access('administration') && (
              <li>
                <Link
                  to="/admin"
                  className={classNames({ "is-active": appState.site_section === "admin" })}
                  onKeyUp={handleNavKeyUp}
                  onClick={handleNavClick}
                >
                  <i className="fa fa-gear"></i>
                  Admin
                </Link>
              </li>
            )}
          </ul>
        </nav>
        <div className="content">
          {props.children}
        </div>
      </main>
      <AudienceUnsavedModal
        isShowing={showUnsavedExplorerModal}
        hide={() => { setShowUnsavedExplorerModal(false) }}
        onClickContinueHandler={handleContinue}
        className="ErrorModal__Content"
        overlayClassName="ErrorModal__Overlay"
        ariaHideApp={false}
      ></AudienceUnsavedModal>
    </div>
  );
}

export function MinimalTemplate(props) {
  const fixtureModeEnabled = localStorage.getItem("useFixtures") === "true";

  return (
    <div className="app minimal-template">
      <header>
        {fixtureModeEnabled && (
          <React.Fragment>
            <i className="spacer" />
            <span className="fixture-mode">Using Demo Data</span>
          </React.Fragment>
        )}

        <i className="spacer" />

            <i className='spacer' />

            <div className="controls">
              <Notifications />
            <UserInfo keepOpen={ false } />
            </div>

          </header>
          <main>
            <div className="content">
              {props.children}
            </div>
          </main>
    </div>
  );
}
